import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    effect,
    inject,
    input,
    InputSignal,
    OnInit,
    Signal,
    viewChild,
} from "@angular/core";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { MatSort, MatSortHeader, MatSortModule } from "@angular/material/sort";
import { MatTableDataSource, MatTableModule } from "@angular/material/table";
import { Router, RouterLink } from "@angular/router";
import { Nullable } from "@playcharge/common/models/backend/nullable";
import { BackOfficeOfferViewModel } from "@playcharge/common/models/backend/offers/backoffice/backoffice-offer-view-model";
import { CopyOfferDataEditModel } from "@playcharge/common/models/backend/offers/backoffice/copy-offer-data-edit-model";
import { ToastrService } from "ngx-toastr";
import { mergeMap, of, EMPTY } from "rxjs";
import { ComponentRoutes } from "../../../models/constants/component-routes";
import { AppsStore } from "../../../stores/offers/apps.store";
import { ForYouStore } from "../../../stores/offers/for-you.store";
import { GamesStore } from "../../../stores/offers/games.store";
import { ConfirmDialogComponent } from "../../dialogs/confirm-dialog/confirm-dialog.component";
import { CopyOfferDialogComponent } from "../../dialogs/copy-offer-dialog/copy-offer-dialog.component";
import { FileInputDialogComponent } from "../../dialogs/file-input-dialog/file-input-dialog.component";
import { CommonModule } from "@angular/common";
import { MatButton } from "@angular/material/button";
import { MatCheckbox } from "@angular/material/checkbox";
import { MatIcon } from "@angular/material/icon";
import { MatMenuModule } from "@angular/material/menu";
import { FlagToStringPipe } from "../../../pipes/flagToString.pipe";
import { Platform } from "@playcharge/common/models/backend/auth/platform";

@Component({
    selector: "app-offers-table",
    standalone: true,
    imports: [
        CommonModule,
        MatTableModule,
        MatButton,
        MatIcon,
        RouterLink,
        MatSort,
        MatSortHeader,
        MatSortModule,
        MatCheckbox,
        MatMenuModule,
        FlagToStringPipe,
    ],
    templateUrl: "./offers-table.component.html",
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OffersTableComponent implements AfterViewInit {

    constructor() {
        effect(() => (this.dataSource.data = this.offers()), 
        {allowSignalWrites: true});        
    }
    private _matDialog: MatDialog = inject(MatDialog);

    protected _toastrService: ToastrService = inject(ToastrService);
    protected _router: Router = inject(Router);

    public offersStore: InputSignal<AppsStore | GamesStore | ForYouStore> = input.required<
        AppsStore | GamesStore | ForYouStore
    >();

    private _matSort: Signal<Nullable<MatSort>> = viewChild(MatSort);

    public displayedColumns: string[] = ["id", "title", "totalCashReward", "totalExpReward", "platform", "isPublished", "isActive", "actions"];
    public dataSource: MatTableDataSource<BackOfficeOfferViewModel> =
        new MatTableDataSource<BackOfficeOfferViewModel>();

    public ComponentRoutes: typeof ComponentRoutes = ComponentRoutes;
    public Platform: typeof Platform = Platform;

    public offers = input.required<BackOfficeOfferViewModel[]>();

    public ngAfterViewInit(): void {
        this.dataSource.filterPredicate = this.filterPredicate;

        this.dataSource.sort = this._matSort()!;
    }

    public publishOffer(offerId: number): void {
        this._matDialog
            .open(ConfirmDialogComponent, {
                data: {
                    message: "Confirm your action to publish this offer.",
                },
            })
            .afterClosed()
            .pipe(
                mergeMap((confirmed: Nullable<boolean>) => {
                    if (confirmed) {
                        return this.offersStore().publishOffer(offerId);
                    }
                    return of(null);
                }),
            )
            .subscribe({
                next: (offer: Nullable<BackOfficeOfferViewModel>) => {
                    if (offer) {
                        this._toastrService.success("Successfully published");
                    }
                },
                error: (error) => {
                    this._toastrService.error(error.error.description);
                },
            });
    }

    public archiveOffer(offerId: number): void {
        this._matDialog
            .open(ConfirmDialogComponent, {
                data: {
                    message: "Confirm your action to archive this offer.",
                },
            })
            .afterClosed()
            .pipe(
                mergeMap((confirmed: Nullable<boolean>) => {
                    if (confirmed) {
                        return this.offersStore().archiveOffer(offerId);
                    }
                    return of(null);
                }),
            )
            .subscribe({
                next: (offer: Nullable<BackOfficeOfferViewModel>) => {
                    if (offer) {
                        this._toastrService.success("Successfully archived");
                    }
                },
                error: (error) => {
                    this._toastrService.error(error.error.description);
                },
            });
    }

    public copyOffer(sourceOfferId: number): void {
        this.copyOfferDataInternal(sourceOfferId, this.offersStore().entities())
            .afterClosed()
            .pipe(
                mergeMap((data: Nullable<CopyOfferDataEditModel>) => {
                    return data ? this.offersStore().copyOffer(data) : EMPTY;
                }),
            )
            .subscribe({
                next: (offer) =>
                    this._router.navigateByUrl(
                        this.ComponentRoutes.offers.offer.replace(":offerId", offer.id.toString()),
                    ),
                error: (error) => {
                    this._toastrService.error(error.error.description);
                },
            });
    }

    public copyFullOffer(sourceOfferId: number): void {
        this.offersStore().copyFullOffer({ offerId: sourceOfferId, sourceOfferId: sourceOfferId })
            .subscribe({
            next: (offer) =>
                this._router.navigateByUrl(
                    this.ComponentRoutes.offers.offer.replace(":offerId", offer.id.toString()),
                ),
            error: (error) => {
                this._toastrService.error(error.error.description);
            },
        });;
    }

    public validateOffer(sourceOfferId: number): void {
        this.offersStore().validateOffer({ offerId: sourceOfferId, sourceOfferId: sourceOfferId })
            .subscribe({
            next: () => this._toastrService.success("Successfully validated"),
            error: (error) => this._toastrService.error(error.error.description),
        });
    }

    public activateOffer(sourceOfferId: number): void {
        this.offersStore().activateOffer({ offerId: sourceOfferId, sourceOfferId: sourceOfferId })
            .subscribe({
            next: () => this._toastrService.success("Successfully activated"),
            error: (error) => this._toastrService.error(error.error.description),
        });
    }

    public deactivateOffer(sourceOfferId: number): void {
        this.offersStore().deactivateOffer({ offerId: sourceOfferId, sourceOfferId: sourceOfferId })
            .subscribe({
            next: () => this._toastrService.success("Successfully deactivated"),
            error: (error) => this._toastrService.error(error.error.description),
        });
    }

    public copyOfferGoals(sourceOfferId: number): void {
        this.copyOfferDataInternal(sourceOfferId, this.offersStore().entities())
            .afterClosed()
            .pipe(
                mergeMap((data: Nullable<CopyOfferDataEditModel>) => {
                    return data ? this.offersStore().copyOfferGoals(data) : EMPTY;
                }),
            )
            .subscribe({
                next: (offer) =>
                    this._router.navigateByUrl(
                        this.ComponentRoutes.offers.offerGoals.replace(
                            ":offerId",
                            offer.id.toString(),
                        ),
                    ),
                error: (error) => {
                    this._toastrService.error(error.error.description);
                },
            });
    }

    public uploadIdfaCsv(): void {
        this._matDialog
            .open(FileInputDialogComponent, {
                data: {
                    accept: ".csv",
                },
            })
            .afterClosed()
            .pipe(
                mergeMap((file: Nullable<File>) => {
                    return file ? this.offersStore().uploadIdfaCsv(file) : EMPTY;
                }),
            )
            .subscribe({
                next: () => this._toastrService.error("Successfully loaded"),
                error: (error) => {
                    this._toastrService.error(error.error.description);
                },
            });
    }

    private copyOfferDataInternal(
        sourceOfferId: number,
        offers: BackOfficeOfferViewModel[],
    ): MatDialogRef<CopyOfferDialogComponent, Nullable<CopyOfferDataEditModel>> {
        return this._matDialog.open(CopyOfferDialogComponent, {
            data: {
                sourceOfferId: sourceOfferId,
                offers: offers,
            },
        });
    }

    private filterPredicate(offer: BackOfficeOfferViewModel, filter: string): boolean {
        return (
            offer.title?.trim().toLocaleLowerCase().includes(filter.trim().toLowerCase()) ?? false
        );
    }
}
